import { DialogComponent } from '@theme/dialog';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * A full-page quiz popup built on top of the theme's dialog component.
 *
 * Markup expectations (set by sections/quiz.liquid and the quiz blocks):
 * - <quiz-component data-quiz-title data-auto-advance data-require-email>
 * - <dialog ref="dialog"> containing a {% form 'contact' %}
 * - <fieldset ref="steps"-child data-step data-step-type="question|contact"
 *     data-question-header> per step
 * - <button data-option data-option-label aria-checked> per answer
 * - refs: progress, backButton, nextButton, submitButton, error, intro
 *
 * On submit it writes one hidden field per answered question into the contact
 * form, so the store owner receives them as labelled lines in the contact email.
 *
 * @extends DialogComponent
 */
class QuizComponent extends DialogComponent {
  #current = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #advanceTimer;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** Guards against building the product recommendation more than once. */
  #recommendationStarted = false;

  connectedCallback() {
    super.connectedCallback();

    // The submit handler is bound here rather than with an `on:submit`
    // attribute because the {% form %} tag can't carry a colon attribute.
    this.#form = this.querySelector('form');
    this.#form?.addEventListener('submit', this.#handleSubmit);
    this.addEventListener('keydown', this.#onKeydown);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onSectionSelect);
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
    }

    // If the visitor has already completed the quiz this session, reveal the
    // result + code straight away. The flag lives in sessionStorage so it
    // survives the contact-form submit and whatever reload it triggers.
    this.#revealIfComplete();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#advanceTimer);
    this.#form?.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /** Opens the popup and shows the current step. */
  showDialog() {
    super.showDialog();
    this.#goTo(this.#current);
  }

  /** Advances to the next step (called by the "Next" button). */
  next() {
    const steps = this.#steps;
    const current = steps[this.#current];

    if (!this.#isAnswered(current)) {
      this.#showError(this.#errorMessage(current));
      return;
    }

    clearTimeout(this.#advanceTimer);
    this.#goTo(this.#current + 1);
  }

  /** Returns to the previous step (called by the "Back" button). */
  back() {
    clearTimeout(this.#advanceTimer);
    this.#goTo(this.#current - 1);
  }

  /**
   * Selects an answer within the current question (pointer/click).
   * @param {Event} event
   */
  selectOption(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const option = target?.closest('[data-option]');
    const step = option?.closest('[data-step]');
    if (!(option instanceof HTMLElement) || !(step instanceof HTMLElement)) return;

    this.#select(step, option, this.#autoAdvance);
  }

  /**
   * Marks `option` as the chosen answer within `step`.
   * @param {HTMLElement} step
   * @param {HTMLElement} option
   * @param {boolean} advance - Whether to auto-advance to the next step.
   */
  #select(step, option, advance) {
    for (const sibling of step.querySelectorAll('[data-option]')) {
      const selected = sibling === option;
      sibling.setAttribute('aria-checked', selected ? 'true' : 'false');
      sibling.classList.toggle('is-selected', selected);
    }

    this.#clearError();
    this.#updateControls();

    if (advance && this.#current < this.#steps.length - 1) {
      clearTimeout(this.#advanceTimer);
      this.#advanceTimer = setTimeout(() => this.#goTo(this.#current + 1), 280);
    }
  }

  /**
   * Roving radio-group navigation with the arrow keys. Selecting via the
   * keyboard never auto-advances, so the visitor can browse the options.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches('[data-option]')) return;

    const step = target.closest('[data-step]');
    if (!(step instanceof HTMLElement)) return;

    const options = /** @type {HTMLElement[]} */ (Array.from(step.querySelectorAll('[data-option]')));
    const index = options.indexOf(target);
    if (index < 0) return;

    let next = index;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % options.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + options.length) % options.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = options.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextOption = options[next];
    nextOption.focus();
    this.#select(step, nextOption, false);
  };

  /**
   * Validates every step and writes the answers into the contact form. On the
   * final step it records completion in sessionStorage and lets the contact form
   * submit; after the resulting load `#revealIfComplete()` shows the result and
   * discount code. Tying the result to a client-side flag (rather than the
   * server's `form.posted_successfully?`) makes it survive the reload — and any
   * spam-protection / challenge round-trip — that the submission goes through.
   * @param {Event} event
   */
  #handleSubmit = (event) => {
    const form =
      this.#form ??
      /** @type {HTMLFormElement | null} */ (/** @type {HTMLElement} */ (event.target)?.closest('form'));
    if (!form) return;

    const steps = this.#steps;
    for (let i = 0; i < steps.length; i++) {
      if (!this.#isAnswered(steps[i])) {
        event.preventDefault();
        this.#goTo(i);
        this.#showError(this.#errorMessage(steps[i]));
        return;
      }
    }

    this.#writeAnswerFields(form, steps);
    // Persist the chosen answers' product tags *before* the form submits: the
    // submission reloads the page and clears the in-DOM selection, so the
    // recommendation is built from these saved tags on the next load.
    this.#persistSelectionTags(steps);
    // Record completion *before* the form submits, so the flag is persisted even
    // though the submission navigates away. No preventDefault: the contact form
    // posts (delivering the answers), and the result is revealed on the next load.
    this.#markComplete();
  };

  /** sessionStorage key marking that this visitor finished this quiz. */
  get #storageKey() {
    return `quiz-complete:${this.id}`;
  }

  /** Records quiz completion so the result survives the submit and reload. */
  #markComplete() {
    try {
      sessionStorage.setItem(this.#storageKey, 'true');
    } catch {
      // sessionStorage can be unavailable (private mode / quota) — ignore.
    }
  }

  /**
   * Shows the result + discount when the quiz was completed this session (the
   * sessionStorage flag) or the server rendered the posted success state, and
   * scrolls it into view.
   */
  #revealIfComplete() {
    let complete = false;
    try {
      complete = sessionStorage.getItem(this.#storageKey) === 'true';
    } catch {
      complete = false;
    }

    const intro = this.refs.intro;
    const serverPosted = intro instanceof HTMLElement && intro.dataset.state === 'success';
    if (!complete && !serverPosted) return;

    if (intro instanceof HTMLElement) {
      intro.dataset.state = 'success';
      requestAnimationFrame(() => {
        intro.scrollIntoView({ behavior: 'smooth', block: 'center' });
        intro.focus?.({ preventScroll: true });
      });
    }

    this.#renderRecommendation();
  }

  /** Clears the completion flag and reopens the quiz from the first step. */
  retake() {
    try {
      sessionStorage.removeItem(this.#storageKey);
      sessionStorage.removeItem(this.#tagsKey);
      sessionStorage.removeItem(this.#recommendationKey);
    } catch {
      // Ignore — clearing the flag is best-effort.
    }
    this.#recommendationStarted = false;
    const intro = this.refs.intro;
    if (intro instanceof HTMLElement) intro.dataset.state = 'intro';
    this.#current = 0;
    this.showDialog();
  }

  /** @returns {HTMLElement[]} The step elements, in order. */
  get #steps() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-step]')));
  }

  get #autoAdvance() {
    return this.dataset.autoAdvance !== 'false';
  }

  get #requireEmail() {
    return this.dataset.requireEmail === 'true';
  }

  /**
   * Shows the step at `index`, updates the controls and moves focus.
   * @param {number} index
   */
  #goTo(index) {
    const steps = this.#steps;
    if (!steps.length) return;

    this.#current = Math.max(0, Math.min(index, steps.length - 1));

    steps.forEach((step, i) => {
      const active = i === this.#current;
      step.classList.toggle('is-active', active);
      if (active) {
        step.setAttribute('tabindex', '-1');
      } else {
        step.removeAttribute('tabindex');
      }
    });

    this.#updateControls();
    this.#clearError();

    const active = steps[this.#current];
    active.focus?.({ preventScroll: true });
    active.closest('.quiz__viewport')?.scrollTo({ top: 0 });
  }

  /** Syncs progress text and the visibility/state of the footer buttons. */
  #updateControls() {
    const steps = this.#steps;
    const total = steps.length;
    if (!total) return;

    const isFirst = this.#current === 0;
    const isLast = this.#current === total - 1;

    const { progress, backButton, nextButton, submitButton } = this.refs;
    if (progress instanceof HTMLElement) progress.textContent = `Step ${this.#current + 1} of ${total}`;

    toggleHidden(backButton, isFirst);
    toggleHidden(nextButton, isLast);
    toggleHidden(submitButton, !isLast);

    const current = steps[this.#current];
    const isContact = current?.dataset.stepType === 'contact';

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = !this.#isAnswered(current);
    }

    // Lock submit on a final *question* step until it's answered, but leave the
    // contact step's submit enabled (email validity is checked on submit, so a
    // disabled button can't get stuck as the visitor types).
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = !isContact && !this.#isAnswered(current);
    }
  }

  /**
   * @param {HTMLElement | undefined} step
   * @returns {boolean}
   */
  #isAnswered(step) {
    if (!step) return false;

    if (step.dataset.stepType === 'contact') {
      if (!this.#requireEmail) return true;
      const email = step.querySelector('[data-email]');
      return email instanceof HTMLInputElement && isValidEmail(email.value);
    }

    return Boolean(step.querySelector('[data-option][aria-checked="true"]'));
  }

  /**
   * @param {HTMLElement | undefined} step
   * @returns {string}
   */
  #errorMessage(step) {
    if (step?.dataset.stepType === 'contact') return 'Please enter a valid email address.';
    return 'Please choose an answer to continue.';
  }

  /**
   * Writes one hidden `contact[...]` field per answered question.
   * @param {HTMLFormElement} form
   * @param {HTMLElement[]} steps
   */
  #writeAnswerFields(form, steps) {
    for (const stale of form.querySelectorAll('input[data-generated]')) stale.remove();

    let questionIndex = 0;
    for (const step of steps) {
      if (step.dataset.stepType === 'contact') continue;
      questionIndex += 1;

      const header = (step.dataset.questionHeader || `Question ${questionIndex}`).trim();
      const selected = step.querySelector('[data-option][aria-checked="true"]');
      const value = selected instanceof HTMLElement ? selected.dataset.optionLabel ?? '' : '';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('data-generated', '');
      input.name = `contact[${questionIndex}. ${sanitizeKey(header)}]`;
      input.value = value;
      form.appendChild(input);
    }
  }

  /** @param {string} message */
  #showError(message) {
    const { error } = this.refs;
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = false;
  }

  #clearError() {
    const { error } = this.refs;
    if (!(error instanceof HTMLElement)) return;
    error.hidden = true;
    error.textContent = '';
  }

  // ---- Theme editor niceties: open the popup when the merchant selects it ----

  #onSectionSelect = (/** @type {Event} */ event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.showDialog();
  };

  #onBlockSelect = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    this.showDialog();
    const step = target.matches('[data-step]') ? target : target.closest('[data-step]');
    if (step instanceof HTMLElement) {
      const index = this.#steps.indexOf(step);
      if (index >= 0) this.#goTo(index);
    }
  };

  // ---- Tag-based product recommendation ----------------------------------
  //
  // On completion the chosen answers' product tags are saved (see
  // #persistSelectionTags). After the result is revealed, the store is searched
  // — via the predictive Search API restricted to the tag field — once per tag.
  // The product matching the most tags wins, its authoritative data is loaded
  // from /products/<handle>.js, and it's shown + saved to the session.

  /** sessionStorage key holding the product tags from the chosen answers. */
  get #tagsKey() {
    return `quiz-tags:${this.id}`;
  }

  /** sessionStorage key holding the recommended product for this quiz. */
  get #recommendationKey() {
    return `quiz-recommendation:${this.id}`;
  }

  get #recommendEnabled() {
    return this.dataset.recommend !== 'false' && this.refs.recommendation instanceof HTMLElement;
  }

  /** How many matched products to show, clamped to 1–3. */
  get #recommendCount() {
    const count = Number.parseInt(this.dataset.recommendCount ?? '', 10);
    return Number.isFinite(count) ? Math.min(3, Math.max(1, count)) : 1;
  }

  get #currency() {
    return this.dataset.currency || 'USD';
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '{{amount}}';
  }

  /**
   * Saves the (deduped) product tags attached to the chosen answers, so the
   * recommendation can be built after the contact-form submit reloads the page.
   * @param {HTMLElement[]} steps
   */
  #persistSelectionTags(steps) {
    /** @type {string[]} */
    const tags = [];
    for (const step of steps) {
      if (step.dataset.stepType === 'contact') continue;
      const selected = step.querySelector('[data-option][aria-checked="true"]');
      const raw = selected instanceof HTMLElement ? selected.dataset.optionTags ?? '' : '';
      for (const part of raw.split(',')) {
        const tag = part.trim();
        if (tag && !tags.some((existing) => existing.toLowerCase() === tag.toLowerCase())) tags.push(tag);
      }
    }

    try {
      sessionStorage.setItem(this.#tagsKey, JSON.stringify(tags));
    } catch {
      // sessionStorage unavailable — the recommendation is best-effort.
    }
  }

  /** @returns {string[]} The saved answer tags. */
  #readTags() {
    try {
      const raw = sessionStorage.getItem(this.#tagsKey);
      const tags = raw ? JSON.parse(raw) : [];
      return Array.isArray(tags) ? tags.filter((tag) => typeof tag === 'string' && tag) : [];
    } catch {
      return [];
    }
  }

  /**
   * Builds (or restores) the recommendation. Finds the best tag-matched products
   * (up to the configured count) and shows each with photo + price; if nothing
   * matches, falls back to the section's fallback product, or a link to all
   * products when none is set. The resolved outcome is saved to the session.
   * Failures fall back rather than breaking the result screen.
   */
  async #renderRecommendation() {
    if (!this.#recommendEnabled || this.#recommendationStarted) return;
    this.#recommendationStarted = true;

    const container = this.refs.recommendation;
    if (!(container instanceof HTMLElement)) return;

    // Already resolved this session → restore without hitting the network.
    const saved = this.#readOutcome();
    if (saved) {
      container.hidden = false;
      this.#applyOutcome(saved);
      return;
    }

    container.hidden = false;

    const tags = this.#readTags();
    /** @type {QuizRecommendation[]} */
    let products = [];
    if (tags.length) {
      this.#setRecommendationLoading(true);
      try {
        const matches = await this.#findBestProducts(tags, this.#recommendCount);
        const loaded = await Promise.all(matches.map((match) => this.#loadProduct(match)));
        products = loaded.filter((product) => product.url);
      } catch {
        products = [];
      }
    }

    // No tag-matched products (none found, no tags, or a failed search) → fall
    // back to the configured product, or a "browse all products" link.
    const fallback = this.#readFallbackProduct();
    /** @type {QuizOutcome} */
    let outcome;
    if (products.length) {
      outcome = { kind: 'products', items: products };
    } else if (fallback) {
      outcome = { kind: 'products', items: [fallback] };
    } else {
      outcome = { kind: 'browse' };
    }

    this.#saveOutcome(outcome);
    this.#applyOutcome(outcome);
  }

  /**
   * Paints the resolved outcome: the matched product cards, or the "browse all"
   * link.
   * @param {QuizOutcome} outcome
   */
  #applyOutcome(outcome) {
    if (outcome.kind === 'products' && outcome.items.length) {
      this.#paintRecommendations(outcome.items);
      this.dispatchEvent(
        new CustomEvent('quiz:recommendation', {
          bubbles: true,
          detail: { products: outcome.items, product: outcome.items[0] },
        })
      );
    } else {
      this.#showBrowseAll();
    }
  }

  /**
   * Reads the section's fallback product, rendered server-side as JSON.
   * @returns {QuizRecommendation | null}
   */
  #readFallbackProduct() {
    const script = this.querySelector('[data-quiz-fallback]');
    if (!(script instanceof HTMLElement) || !script.textContent) return null;
    try {
      const data = JSON.parse(script.textContent);
      if (!data || typeof data.url !== 'string' || !data.url) return null;
      return {
        handle: '',
        url: data.url,
        title: typeof data.title === 'string' ? data.title : '',
        image: typeof data.image === 'string' ? data.image : '',
        priceFormatted: typeof data.priceFormatted === 'string' ? data.priceFormatted : '',
      };
    } catch {
      return null;
    }
  }

  /** Reveals the "browse all products" fallback link. */
  #showBrowseAll() {
    const { recommendation, recommendationLoader, recommendationList, recommendationBrowse } = this.refs;
    if (!(recommendation instanceof HTMLElement)) return;
    if (recommendationLoader instanceof HTMLElement) recommendationLoader.hidden = true;
    if (recommendationList instanceof HTMLElement) recommendationList.hidden = true;
    if (recommendationBrowse instanceof HTMLElement) recommendationBrowse.hidden = false;
    recommendation.hidden = false;
  }

  /**
   * Searches the store once per tag (predictive Search API, tag field only) and
   * returns up to `count` products, ranked by how many of the chosen tags they
   * match (ties broken by first-seen order).
   * @param {string[]} tags
   * @param {number} count
   * @returns {Promise<QuizProductMatch[]>}
   */
  async #findBestProducts(tags, count) {
    const queries = tags.slice(0, 12);
    const responses = await Promise.allSettled(queries.map((tag) => this.#searchByTag(tag)));

    /** @type {Map<string, QuizProductMatch & { score: number, order: number }>} */
    const candidates = new Map();
    let order = 0;

    for (const response of responses) {
      if (response.status !== 'fulfilled') continue;
      for (const product of response.value) {
        const existing = candidates.get(product.handle);
        if (existing) {
          existing.score += 1;
        } else {
          candidates.set(product.handle, { ...product, score: 1, order: order++ });
        }
      }
    }

    return Array.from(candidates.values())
      .sort((a, b) => b.score - a.score || a.order - b.order)
      .slice(0, Math.max(1, count));
  }

  /**
   * @param {string} tag
   * @returns {Promise<QuizProductMatch[]>}
   */
  async #searchByTag(tag) {
    const base =
      (typeof Theme !== 'undefined' && Theme.routes?.predictive_search_url) || '/search/suggest';
    const url = new URL(`${base}.json`, location.origin);
    url.searchParams.set('q', tag);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][fields]', 'tag');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];

    const data = await response.json();
    /** @type {any[]} */
    const products = data?.resources?.results?.products ?? [];
    return products
      .map((product) => ({
        handle: product.handle || handleFromUrl(product.url),
        url: pathFromUrl(product.url),
        title: product.title ?? '',
        image: product.image ?? product.featured_image ?? '',
        price: String(product.price ?? ''),
      }))
      .filter((product) => product.handle);
  }

  /**
   * Loads authoritative product data (price in minor units, featured image) for
   * the matched product and returns a paint-ready recommendation. Falls back to
   * the search-result fields if the product JSON can't be fetched.
   * @param {QuizProductMatch} match
   * @returns {Promise<QuizRecommendation>}
   */
  async #loadProduct(match) {
    /** @type {any} */
    let data = null;
    try {
      const response = await fetch(`/products/${encodeURIComponent(match.handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (response.ok) data = await response.json();
    } catch {
      data = null;
    }

    const priceFormatted = data
      ? formatMoney(data.price, this.#moneyFormat, this.#currency)
      : this.#formatFallbackPrice(match.price);

    // Prefer the search result's url/title — they already reflect the visitor's
    // locale/market — and take the authoritative price + image from the JSON.
    return {
      handle: match.handle,
      url: match.url || (data && data.url) || `/products/${match.handle}`,
      title: match.title || (data && data.title) || '',
      image: (data && data.featured_image) || match.image || '',
      priceFormatted,
    };
  }

  /**
   * Formats a money string returned by predictive search (e.g. "80.00").
   * @param {string} price
   */
  #formatFallbackPrice(price) {
    const minorUnits = convertMoneyToMinorUnits(price, this.#currency);
    return minorUnits == null ? '' : formatMoney(minorUnits, this.#moneyFormat, this.#currency);
  }

  /** @param {QuizOutcome} outcome */
  #saveOutcome(outcome) {
    try {
      sessionStorage.setItem(this.#recommendationKey, JSON.stringify(outcome));
    } catch {
      // Best-effort.
    }
  }

  /** @returns {QuizOutcome | null} */
  #readOutcome() {
    try {
      const raw = sessionStorage.getItem(this.#recommendationKey);
      if (!raw) return null;
      const outcome = JSON.parse(raw);
      if (!outcome || typeof outcome !== 'object') return null;
      if (outcome.kind === 'browse') return { kind: 'browse' };
      if (outcome.kind === 'products' && Array.isArray(outcome.items)) {
        const items = outcome.items.filter((item) => item && typeof item.url === 'string' && item.url);
        if (items.length) return { kind: 'products', items };
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Renders one card per matched product, cloned from the <template>.
   * @param {QuizRecommendation[]} products
   */
  #paintRecommendations(products) {
    const { recommendation, recommendationList, recommendationTemplate, recommendationBrowse } = this.refs;
    if (
      !(recommendation instanceof HTMLElement) ||
      !(recommendationList instanceof HTMLElement) ||
      !(recommendationTemplate instanceof HTMLTemplateElement)
    ) {
      return;
    }

    // Rebuild from scratch so a retake never stacks old cards onto new ones.
    recommendationList.replaceChildren();

    for (const product of products) {
      const fragment = recommendationTemplate.content.cloneNode(true);
      if (!(fragment instanceof DocumentFragment)) continue;

      const card = fragment.querySelector('[data-card]');
      const image = fragment.querySelector('[data-image]');
      const title = fragment.querySelector('[data-title]');
      const price = fragment.querySelector('[data-price]');

      if (card instanceof HTMLAnchorElement) card.href = product.url;
      if (title instanceof HTMLElement) title.textContent = product.title;
      if (price instanceof HTMLElement) price.textContent = product.priceFormatted;

      if (image instanceof HTMLImageElement) {
        const src = sizedImageUrl(product.image, 640);
        if (src) {
          image.src = src;
          image.alt = product.title;
        } else {
          const media = image.closest('.quiz__product-media');
          if (media instanceof HTMLElement) media.hidden = true;
        }
      }

      recommendationList.appendChild(fragment);
    }

    this.#setRecommendationLoading(false);
    if (recommendationBrowse instanceof HTMLElement) recommendationBrowse.hidden = true;
    recommendation.hidden = false;
    recommendationList.hidden = false;
  }

  /** @param {boolean} loading */
  #setRecommendationLoading(loading) {
    const { recommendationLoader, recommendationList, recommendationBrowse } = this.refs;
    if (recommendationLoader instanceof HTMLElement) recommendationLoader.hidden = !loading;
    if (loading) {
      if (recommendationList instanceof HTMLElement) recommendationList.hidden = true;
      if (recommendationBrowse instanceof HTMLElement) recommendationBrowse.hidden = true;
    }
  }
}

/**
 * @param {Element | Element[] | undefined} element
 * @param {boolean} hidden
 */
function toggleHidden(element, hidden) {
  if (element instanceof HTMLElement) element.toggleAttribute('hidden', hidden);
}

/** @param {string} value */
function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Strips characters that would break the `contact[...]` field name.
 * @param {string} value
 */
function sanitizeKey(value) {
  return value.replace(/[[\]]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Extracts a product handle from a storefront URL.
 * @param {string | undefined} url
 * @returns {string}
 */
function handleFromUrl(url) {
  if (!url) return '';
  try {
    const match = new URL(url, location.origin).pathname.match(/\/products\/([^/]+)/);
    const handle = match?.[1];
    return handle ? decodeURIComponent(handle) : '';
  } catch {
    return '';
  }
}

/**
 * Returns just the pathname of a URL, dropping predictive-search tracking params.
 * @param {string | undefined} url
 * @returns {string}
 */
function pathFromUrl(url) {
  if (!url) return '';
  try {
    return new URL(url, location.origin).pathname;
  } catch {
    return url;
  }
}

/**
 * Adds a `width` parameter to a Shopify CDN image URL so a right-sized image is
 * served. Protocol-relative CDN URLs (//cdn.shopify.com/…) are resolved too.
 * @param {string} src
 * @param {number} width
 * @returns {string}
 */
function sizedImageUrl(src, width) {
  if (!src) return '';
  try {
    const url = new URL(src, location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
}

/**
 * @typedef {object} QuizProductMatch
 * @property {string} handle
 * @property {string} url
 * @property {string} title
 * @property {string} image
 * @property {string} price - Money string from predictive search (display fallback).
 */

/**
 * @typedef {object} QuizRecommendation
 * @property {string} handle
 * @property {string} url
 * @property {string} title
 * @property {string} image
 * @property {string} priceFormatted
 */

/**
 * The resolved recommendation outcome: one or more products to show, or a
 * "browse all" link.
 * @typedef {{ kind: 'products', items: QuizRecommendation[] } | { kind: 'browse' }} QuizOutcome
 */

if (!customElements.get('quiz-component')) {
  customElements.define('quiz-component', QuizComponent);
}
